import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Columns3,
  User,
  AlertTriangle,
  ChevronsUpDown,
} from 'lucide-react'
import { BrushCleaner } from '@/components/icons/BrushCleaner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Table } from '@tanstack/react-table'
import { InventoryItem } from '../interfaces/inventory'
import { getSuppliers } from '@/api/providersAPI'
import { useTableContext } from './TableContext'

interface InventoryTableHeaderProps {
  table: Table<InventoryItem>
  onSearchChange: (_value: string) => void
  searchValue: string
}

export function InventoryTableHeader({
  table,
  onSearchChange,
  searchValue,
}: InventoryTableHeaderProps) {
  const { filters, setFilters } = useTableContext()

  const [providerSearch, setProviderSearch] = useState<string>('')

  // Get filter values from context
  const selectedProvider = (filters.supplier as string) || ''
  const selectedProviderName = (filters.supplierName as string) || ''
  const expirationFilters = (filters.expiration as {
    todos: boolean
    urgente: boolean
    alerta: boolean
    normal: boolean
  }) || {
    todos: true,
    urgente: false,
    alerta: false,
    normal: false,
  }

  // Función para traducir nombres de columnas
  const getColumnDisplayName = (columnId: string): string => {
    const columnTranslations: Record<string, string> = {
      name: 'Nombre',
      quantity: 'Cantidad',
      containers: 'Contenedores',
      suppliers: 'Proveedores',
      expirationDate: 'Vencimiento',
    }
    return columnTranslations[columnId] || columnId
  }

  // Query para obtener suppliers de la API (limitado a 10 para mejor performance)
  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error,
  } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const result = await getSuppliers({
        page: 1,
        per_page: 10, // Limitado a 10 suppliers para mejor performance
        search: '',
      })
      return result
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  })

  // Transformar datos de suppliers para mostrar
  const providerOptions = Array.isArray(suppliers)
    ? suppliers.map(
        (supplier: { id: number; name?: string; title?: string }) => ({
          id: supplier.id,
          name: supplier.name || supplier.title || 'Sin nombre',
        })
      )
    : []

  const handleClearFilters = () => {
    onSearchChange('')
    table.resetColumnFilters()
    setProviderSearch('')
    setFilters({
      ...filters,
      supplier: '',
      supplierName: '',
      expiration: {
        todos: true,
        urgente: false,
        alerta: false,
        normal: false,
      },
    })
  }

  // Verificar si hay filtros aplicados
  const hasActiveFilters =
    searchValue.trim() !== '' ||
    selectedProvider !== '' ||
    !expirationFilters.todos

  const handleExpirationFilterChange = (
    filterType: string,
    checked: boolean
  ) => {
    if (filterType === 'todos') {
      // Si selecciona "Todos", desmarcar los demás
      setFilters({
        ...filters,
        expiration: {
          todos: checked,
          urgente: false,
          alerta: false,
          normal: false,
        },
      })
    } else {
      // Si selecciona cualquier otro, desmarcar "Todos"
      const newExpirationFilters = {
        ...expirationFilters,
        todos: false,
        [filterType]: checked,
      }
      setFilters({
        ...filters,
        expiration: newExpirationFilters,
      })
    }
  }

  const handleProviderChange = (
    providerId: string | number,
    providerName?: string
  ) => {
    setFilters({
      ...filters,
      supplier: providerId.toString(),
      supplierName: providerName || providerId.toString(), // Guardar el nombre para mostrar en la UI
    })
    setProviderSearch('')
  }

  // Mock provider list - in real app this would come from API
  const filteredProviders = isLoadingSuppliers
    ? [{ id: 'loading', name: 'Cargando...' }]
    : providerOptions.filter((provider) =>
        provider.name.toLowerCase().includes(providerSearch.toLowerCase())
      )

  return (
    <>
      {/* Fila única - Búsqueda y todos los filtros */}
      <div className="flex items-center py-2">
        {/* Búsqueda extendida */}
        <div className="relative mr-2 flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="h-9 w-full bg-white pl-8"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar productos..."
            value={searchValue}
          />
        </div>

        {/* Filtros y controles alineados a la derecha */}
        <div className="flex items-center space-x-2">
          {/* Columnas */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="h-9 min-w-fit justify-start bg-white px-3 font-normal text-[#262626]"
                style={{ fontSize: '14px' }}
                variant="outline"
              >
                <Columns3 className="mr-2 h-4 w-4" />
                Columnas
                <ChevronsUpDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-40 p-3">
              <div className="space-y-2">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <div
                        className="flex items-center space-x-2"
                        key={column.id}
                      >
                        <Checkbox
                          checked={column.getIsVisible()}
                          id={column.id}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        />
                        <label
                          className="cursor-pointer text-sm font-normal"
                          htmlFor={column.id}
                        >
                          {getColumnDisplayName(column.id)}
                        </label>
                      </div>
                    )
                  })}
              </div>
            </PopoverContent>
          </Popover>

          {/* Filas por página */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="h-9 min-w-fit justify-start bg-white px-3 font-normal text-[#262626]"
                style={{ fontSize: '14px' }}
                variant="outline"
              >
                {table.getState().pagination.pageSize} por página
                <ChevronsUpDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-32 p-3">
              <div className="space-y-2">
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <div className="flex items-center space-x-2" key={pageSize}>
                    <Checkbox
                      checked={
                        table.getState().pagination.pageSize === pageSize
                      }
                      id={`pageSize-${pageSize}`}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          table.setPageSize(pageSize)
                        }
                      }}
                    />
                    <label
                      className="cursor-pointer text-sm font-normal"
                      htmlFor={`pageSize-${pageSize}`}
                    >
                      {pageSize}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {/* Filtro de Proveedor */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="h-9 min-w-fit justify-start bg-white px-3 font-normal text-[#262626]"
                style={{ fontSize: '14px' }}
                variant="outline"
              >
                <User className="mr-1 h-4 w-4" />
                <span className="whitespace-nowrap">
                  {selectedProvider === 'todos' || !selectedProvider
                    ? 'Proveedor'
                    : selectedProviderName || selectedProvider}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-48 p-3" side="bottom">
              <div className="space-y-3">
                {/* Search bar for providers */}
                <div className="relative">
                  <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="h-9 pl-8"
                    onChange={(event) => setProviderSearch(event.target.value)}
                    placeholder="Buscar proveedor..."
                    value={providerSearch}
                  />
                </div>

                {/* Provider options */}
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {!isLoadingSuppliers && (
                    <div
                      className="cursor-pointer rounded px-2 py-1.5 text-sm font-normal hover:bg-gray-100"
                      onClick={() => {
                        handleProviderChange('todos', 'Todos los proveedores')
                      }}
                    >
                      Todos los proveedores
                    </div>
                  )}

                  {isLoadingSuppliers ? (
                    <div className="px-2 py-1.5 text-sm text-gray-500">
                      Cargando proveedores...
                    </div>
                  ) : (
                    filteredProviders.map((provider) => (
                      <div
                        className="cursor-pointer rounded px-2 py-1.5 text-sm font-normal hover:bg-gray-100"
                        key={provider.id}
                        onClick={() => {
                          handleProviderChange(provider.id, provider.name)
                        }}
                      >
                        {provider.name}
                      </div>
                    ))
                  )}

                  {!isLoadingSuppliers &&
                    filteredProviders.length === 0 &&
                    providerSearch && (
                      <div className="px-2 py-1.5 text-sm text-gray-500">
                        No se encontraron proveedores
                      </div>
                    )}
                  {error && (
                    <div className="px-2 py-1.5 text-sm text-red-500">
                      Error al cargar proveedores
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Filtro de Vencimiento - Checkboxes simplificado */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                className="h-9 min-w-fit justify-start bg-white px-3 font-normal text-[#262626]"
                style={{ fontSize: '14px' }}
                variant="outline"
              >
                <AlertTriangle className="mr-1 h-4 w-4" />
                <span className="whitespace-nowrap">Vencimiento</span>
                <ChevronsUpDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[--radix-popover-trigger-width] p-3"
              side="bottom"
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={expirationFilters.todos}
                    id="todos"
                    onCheckedChange={(checked) =>
                      handleExpirationFilterChange('todos', !!checked)
                    }
                  />
                  <label
                    className="cursor-pointer text-sm font-normal"
                    htmlFor="todos"
                  >
                    Todas
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={expirationFilters.urgente}
                    id="urgente"
                    onCheckedChange={(checked) =>
                      handleExpirationFilterChange('urgente', !!checked)
                    }
                  />
                  <label
                    className="cursor-pointer text-sm font-normal"
                    htmlFor="urgente"
                  >
                    Urgente
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={expirationFilters.alerta}
                    id="alerta"
                    onCheckedChange={(checked) =>
                      handleExpirationFilterChange('alerta', !!checked)
                    }
                  />
                  <label
                    className="cursor-pointer text-sm font-normal"
                    htmlFor="alerta"
                  >
                    Alerta
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={expirationFilters.normal}
                    id="normal"
                    onCheckedChange={(checked) =>
                      handleExpirationFilterChange('normal', !!checked)
                    }
                  />
                  <label
                    className="cursor-pointer text-sm font-normal"
                    htmlFor="normal"
                  >
                    Normal
                  </label>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Limpiar filtros - Solo mostrar si hay filtros aplicados */}
          {hasActiveFilters && (
            <Button
              className="h-9 w-9 flex-shrink-0 border-red-600 bg-red-600 p-0 hover:border-red-700 hover:bg-red-700"
              onClick={handleClearFilters}
              size="sm"
              title="Limpiar filtros"
              variant="outline"
            >
              <BrushCleaner className="h-4 w-4 text-white" />
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
